import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, AlertCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../contexts/AuthContext';
import AuthService from '../services/AuthService';

/**
 * Register page component
 * Handles new user registration with email verification
 */
const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSchool, setCheckingSchool] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Redirect to home if already logged in
  useEffect(() => {
    if (user) {
      navigate('/school');
    }
  }, [user, navigate]);
  
  // Check if a domain exists and if signups are allowed
  const checkSchoolStatus = async (email) => {
    if (!email.includes('@') || !email.endsWith('.edu')) {
      return { exists: false, isSignupPaused: false };
    }
  
    try {
      setCheckingSchool(true);
      const domain = email.split('@')[1];
      
      // Make sure the API URL is correct and properly formatted
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/schools/check-status?domain=${encodeURIComponent(domain)}`;
      console.log('Checking school status at URL:', apiUrl);
      
      const response = await fetch(apiUrl);
      
      // Parse the response regardless of status
      const data = await response.json();
      console.log('School status check response:', response.status, data);
      
      if (!response.ok) {
        // If the server returns an error, log it but don't throw
        console.warn('School status check returned non-200 status:', response.status, data);
        
        // For compatibility, if the response has exists and isSignupPaused, use those
        if (data && 'exists' in data && 'isSignupPaused' in data) {
          return data;
        }
        
        // Otherwise fall back to a default value
        return { exists: true, isSignupPaused: false };
      }
      
      return data;
    } catch (error) {
      console.error('Error checking school status:', error);
      // Return a default value instead of throwing
      return { exists: true, isSignupPaused: false };
    } finally {
      setCheckingSchool(false);
    }
  };
  
  const validateForm = () => {
    // Check if all fields are filled
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return false;
    }
    
    // Validate email format (.edu domain required)
    if (!email.endsWith('.edu')) {
      toast.error('Please use a valid .edu email address');
      return false;
    }
    
    // Check if passwords match
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    
    // Check password length
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Check if school exists and if signups are allowed
      console.log('Checking school status for email:', email);
      const schoolStatus = await checkSchoolStatus(email);
      console.log('School status result:', schoolStatus);
      
      // If school doesn't exist
      if (!schoolStatus.exists) {
        toast.error('Your school is not supported yet. Please contact administrators or join the waitlist.');
        navigate(`/waitlist?email=${encodeURIComponent(email)}`);
        return;
      }
      
      // If signups are paused, redirect to waitlist
      if (schoolStatus.isSignupPaused) {
        console.log('Signups are paused, redirecting to waitlist');
        // Use toast.error instead of toast here to ensure consistency
        toast.error('Signups are currently paused for your school. You have been redirected to the waitlist.');
        navigate(`/waitlist?email=${encodeURIComponent(email)}`);
        return;
      }
      
      // Otherwise, proceed with registration
      await AuthService.register({
        name,
        email,
        password,
      });
      
      setRegistrationComplete(true);
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Failed to register. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // If registration is complete, show verification message
  if (registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="flex justify-center">
              <div className="text-4xl font-bold text-red-500">EDX</div>
              <div className="text-4xl font-bold text-black dark:text-white">Red</div>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              Registration Successful!
            </h2>
          </div>
          
          <div className="mt-2 p-4 bg-green-100 dark:bg-green-800 rounded-md">
            <p className="text-sm text-green-800 dark:text-green-200">
              A verification email has been sent to <strong>{email}</strong>. 
              Please check your inbox and follow the instructions to verify your account.
            </p>
          </div>
          
          <div className="mt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Didn't receive the email? Check your spam folder or{' '}
              <button 
                onClick={() => {
                  setRegistrationComplete(false);
                  toast.success('You can try registering again');
                }}
                className="font-medium text-red-600 hover:text-red-500"
              >
                try again
              </button>
            </p>
          </div>
          
          <div className="mt-6">
            <Link
              to="/login"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="text-4xl font-bold text-red-500">EDX</div>
            <div className="text-4xl font-bold text-black dark:text-white">Red</div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-red-600 hover:text-red-500">
              Sign in
            </Link>
          </p>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-800 rounded-md p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                You must use a valid <strong>.edu</strong> email address to register.
              </p>
            </div>
          </div>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="name" className="sr-only">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 rounded-t-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                placeholder="Full Name"
                disabled={loading || checkingSchool}
              />
            </div>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                placeholder="Email address (.edu required)"
                disabled={loading || checkingSchool}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                disabled={loading || checkingSchool}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="sr-only">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 rounded-b-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                placeholder="Confirm Password"
                disabled={loading || checkingSchool}
              />
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading || checkingSchool}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                (loading || checkingSchool) ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                {(loading || checkingSchool) ? (
                  <Loader className="h-5 w-5 text-red-500 group-hover:text-red-400 animate-spin" />
                ) : (
                  <UserPlus className="h-5 w-5 text-red-500 group-hover:text-red-400" />
                )}
              </span>
              {loading ? 'Creating account...' : checkingSchool ? 'Checking school...' : 'Sign up'}
            </button>
          </div>
        </form>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            By signing up, you agree to the{' '}
            <Link to="/terms" className="font-medium text-red-600 hover:text-red-500">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="font-medium text-red-600 hover:text-red-500">
              Privacy Policy
            </Link>
          </p>
        </div>
        
        {/* New section promoting the waitlist */}
        <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            Is your school not supported yet?
          </h3>
          <Link
            to="/waitlist"
            className="w-full flex justify-center py-2 px-4 border border-red-300 dark:border-red-800 text-sm font-medium rounded-md text-red-600 dark:text-red-400 bg-transparent hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Join the Waitlist
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;